import { Injectable, inject } from '@angular/core'
import { FormGroup } from '@angular/forms'

import { orderBy as _orderBy } from 'lodash/fp'

import { Observable, distinctUntilChanged, map, of, startWith, switchMap } from 'rxjs'

import {
  CollectionPrototypeInstanceMeta,
  DataSource,
  DataSourceType,
  FormFieldsEnum,
  getOptionsFromEnumeration,
  getOptionsFromProcessData,
  localizeOptionValue,
  localizeString,
} from '@flowx/core-sdk'

import { FlxFormsService } from './flx-forms.service'

// SELECT (prefixed) flowxUuid -> resolved source key (enum name / process-data
// key). Only SELECT registers; non-SELECT children miss the lookup and get
// `of([])`. Every select registers (root + intermediate) -> enum and
// process-data cascade at any depth. Mirrors the React useDataSourceOptions hook.
const enumNameByFlowxUuid = new Map<string, string>()
const processDataKeyByFlowxUuid = new Map<string, string>()

@Injectable({
  providedIn: 'root',
})
export class FlxFieldOptionService {
  #formService = inject(FlxFormsService)

  getOptionsObs(
    dataSource: DataSource,
    processInstanceUuid: string,
    formGroup: FormGroup,
    componentUuid?: string,
    prototypeInstanceMeta?: CollectionPrototypeInstanceMeta[],
    reusableContext?: string,
    componentIdentifier?: FormFieldsEnum
  ): Observable<any> {
    const options = dataSource?.options
    switch (dataSource?.sourceType) {
      case DataSourceType.PROCESS_DATA:
        return this.#getOptionsFromProcessDataHierarchy(
          dataSource,
          processInstanceUuid,
          componentUuid,
          prototypeInstanceMeta,
          reusableContext,
          componentIdentifier
        )
      case DataSourceType.ENUMERATION:
        return this.#getOptionsFromEnumerationHierarchy(
          dataSource,
          formGroup,
          componentUuid,
          prototypeInstanceMeta,
          reusableContext,
          componentIdentifier
        )
      case DataSourceType.STATIC:
        return of(_orderBy((val) => parseInt(val.order as string, 10), 'asc', options)).pipe(
          map((options) =>
            options.map((option) => ({
              ...option,
              label: localizeString(option.label),
              value: localizeOptionValue(option.value),
            }))
          )
        )
      default:
        return of([])
    }
  }

  #getOptionsFromEnumerationHierarchy(
    dataSource: DataSource,
    formGroup: FormGroup,
    componentUuid: string,
    prototypeInstanceMeta?: CollectionPrototypeInstanceMeta[],
    reusableContext?: string,
    componentIdentifier?: FormFieldsEnum
  ): Observable<any> {
    const isSelect = componentIdentifier === FormFieldsEnum.SELECT
    const parentFlowxUuid = dataSource?.nomenclator?.parentFlowxUuid
    const ownName = dataSource?.nomenclator?.name as string | undefined

    // Root enum: any field type fetches; only SELECT also registers.
    if (ownName && !parentFlowxUuid) {
      if (isSelect && componentUuid) {
        const registryKey = reusableContext ? `${reusableContext}_${componentUuid}` : componentUuid
        enumNameByFlowxUuid.set(registryKey, ownName)
      }
      return getOptionsFromEnumeration(ownName, null)
    }

    // Cascading enum: SELECT child only. No grandchild cap — see switchMap.
    if (!isSelect || !parentFlowxUuid) {
      return of([])
    }

    // Gate by parent's per-row INSTANCE flowxUuid (registry key), not template parentFlowxUuid.
    const parentFieldName = this.#formService.getFormControlField(
      parentFlowxUuid,
      prototypeInstanceMeta,
      reusableContext
    )?.name
    if (!parentFieldName) {
      return of([])
    }
    const prefixedParentInstanceUuid = reusableContext
      ? `${reusableContext}_${parentFieldName}`
      : parentFieldName
    const parentEnumName = enumNameByFlowxUuid.get(prefixedParentInstanceUuid)
    if (!parentEnumName) {
      return of([])
    }

    const parentControl = formGroup?.controls[parentFieldName]
    if (!parentControl) {
      console.warn(
        `Control with flowxUuid ${parentFlowxUuid} is configured as parent for an enumeration data source, but no matching form control was found`
      )
      return of([])
    }

    // Propagate the ROOT enum name down. Backend addresses every node as
    // `${root}?childEnumName=<node>`, so parentName stays the root at any depth
    // — never the intermediate's resolved value.
    if (componentUuid) {
      const registryKey = reusableContext ? `${reusableContext}_${componentUuid}` : componentUuid
      enumNameByFlowxUuid.set(registryKey, parentEnumName)
    }

    return parentControl.valueChanges.pipe(
      startWith(parentControl?.value),
      distinctUntilChanged((previous, current) => {
        if (!previous) {
          return false
        }
        const sameActualValue = previous?.value === current
        const sameID = previous?.id === current?.id
        const sameValue = previous?.value === current?.value
        const sameNomenclator = previous?.nomenclatorId === current?.nomenclatorId

        return sameActualValue || (sameID && sameValue && sameNomenclator)
      }),
      switchMap((parentValue) =>
        getOptionsFromEnumeration(parentValue?.nomenclatorId, parentEnumName)
      )
    )
  }

  #getOptionsFromProcessDataHierarchy(
    dataSource: DataSource,
    processInstanceUuid: string,
    componentUuid: string,
    prototypeInstanceMeta?: CollectionPrototypeInstanceMeta[],
    reusableContext?: string,
    componentIdentifier?: FormFieldsEnum
  ): Observable<any> {
    const isSelect = componentIdentifier === FormFieldsEnum.SELECT
    const parentFlowxUuid = dataSource?.processData?.parentFlowxUuid
    const ownKey = dataSource?.processData?.key as string | undefined

    // Root process-data: any field type fetches; only SELECT also registers.
    if (ownKey && !parentFlowxUuid) {
      if (isSelect && componentUuid) {
        const registryKey = reusableContext ? `${reusableContext}_${componentUuid}` : componentUuid
        processDataKeyByFlowxUuid.set(registryKey, ownKey)
      }
      return getOptionsFromProcessData(
        dataSource,
        processInstanceUuid,
        undefined,
        prototypeInstanceMeta
      )
    }

    // Cascading process-data: SELECT child only. No grandchild cap.
    if (!isSelect || !parentFlowxUuid) {
      return of([])
    }

    // Gate by parent's per-row INSTANCE flowxUuid (registry key), not template parentFlowxUuid.
    const parentField = this.#formService.getFormControlField(
      parentFlowxUuid,
      prototypeInstanceMeta,
      reusableContext
    )
    const parentInstanceUuid = parentField?.name
    const prefixedParentInstanceUuid =
      parentInstanceUuid && reusableContext
        ? `${reusableContext}_${parentInstanceUuid}`
        : parentInstanceUuid
    if (!prefixedParentInstanceUuid || !processDataKeyByFlowxUuid.has(prefixedParentInstanceUuid)) {
      return of([])
    }

    // Cascade reads the parent's SELECTED VALUE, which lives at the parent
    // field's data-binding key (config.key / dataKey), not its options-source
    // key (dataSource.processData.key). Core resolves `${ownKey}.${selected}`.
    const parentDataKey = parentField?.dataKey
    if (!parentDataKey) {
      return of([])
    }

    // Register own key so children cascade at any depth.
    if (ownKey && componentUuid) {
      const registryKey = reusableContext ? `${reusableContext}_${componentUuid}` : componentUuid
      processDataKeyByFlowxUuid.set(registryKey, ownKey)
    }

    return getOptionsFromProcessData(
      dataSource,
      processInstanceUuid,
      parentDataKey,
      prototypeInstanceMeta
    )
  }
}
